import { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetDomains } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import DomainHttpService from '../../../services/right/domainHttpService';
import DomainForm from './form/DomainForm';
import { IApiError } from '../../../@types/other';
import { setDomainMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import DomainModelHelper from '../../../@types/model/masterData/domain/domainModelHelper';
import { IDomainFormValues } from '../../../@types/model/masterData/domain/domainFormValues';
import UserHelper from '../../../services/helper/userHelper';
import { IDomain } from '../../../@types/model/masterData/domain/domain';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const DomainScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const domains = useAppSelector(x => x.masterData.domains);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedDomain, setSelectedDomain] = useState<IDomain | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingDomain, setDeletingDomain] = useState<IDomain | null>(null);
    
    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await DomainHttpService.getDomainData();
                dispatch(dataSetDomains(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading domain data.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IDomainFormValues) => {
        setIsLoading(true);

        try {
            const upsert = DomainModelHelper.createUpsert(values);
            const res = await DomainHttpService.addOrUpdateDomain(upsert);
            const updatedList = upsertArrayElement(domains, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDomains(updatedList));
            await setDomainMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedDomain ? 'Domain updated successfully.' : 'Domain added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating domain data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeDomain = async () => {
        if (!deletingDomain) return;
        const updatedDomain = { ...deletingDomain, isActive: false };
        setIsLoading(true);
        try {
            const res = await DomainHttpService.deleteDomain(updatedDomain.id);
            const updatedList = upsertArrayElement(domains, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDomains(updatedList));
            await setDomainMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Domain deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting domain.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedDomain(null);
    };

    const edit = (selectedRow : IDomain) => {
        setSelectedDomain(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IDomain) : void => {
        setDeletingDomain(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingDomain(null);
    };

    const deleteButtonTooltip = (row : IDomain) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Domain', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo<IDomainFormValues>(() => {
        return DomainModelHelper.createFormValues(selectedDomain);
    }, [selectedDomain, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IDomain>
                        
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Domain', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={(row : IDomain) => !userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Domain', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IDomain) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                    
                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Domain Name', field: 'domainName', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={domains}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedDomain ? 'Edit Domain' : 'Add Domain'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                >
                    <DomainForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Domain'}
                open={!!deletingDomain}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeDomain}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default DomainScreen;
